import type { CityConfig } from "../city-template/types";

export const losAngelesConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Los Angeles",
  state: "California",
  stateAbbr: "CA",
  slug: "los-angeles",
  route: "/los-angeles",
  title: "Emergency Passport Los Angeles, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Los Angeles, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for LA County and Southern California travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Los Angeles, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Los Angeles and Southern California travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Los%20Angeles%20skyline%20at%20golden%20hour%20with%20downtown%20skyscrapers%2C%20Griffith%20Observatory%20visible%20on%20the%20hillside%2C%20warm%20sunset%20light%20reflecting%20off%20modern%20glass%20buildings%2C%20Hollywood%20Hills%20in%20the%20background%2C%20clear%20sky%20with%20warm%20amber%20and%20orange%20tones%2C%20professional%20cityscape%20photography%2C%20cinematic%20wide%20angle%20view%2C%20no%20visible%20text%20or%20people%2C%20clean%20and%20modern%20urban%20scene&width=1800&height=700&seq=la-hero-001&orientation=landscape",
  heroImageAlt: "Los Angeles skyline at golden hour with downtown skyscrapers and Griffith Observatory",
  geoPosition: "34.0522;-118.2437",
  geoPlacename: "Los Angeles, California",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — visit in person at 11000 Wilshire Blvd. No drive distance needed for LA residents.",
  },
  nearbyCitiesZips: [
    { name: "Downtown LA", zips: "90012–90017" },
    { name: "Hollywood", zips: "90028, 90038" },
    { name: "Beverly Hills", zips: "90210, 90211" },
    { name: "Santa Monica", zips: "90401–90405" },
    { name: "West Hollywood", zips: "90046, 90069" },
    { name: "Pasadena", zips: "91101–91105" },
    { name: "Glendale", zips: "91201–91206" },
    { name: "Burbank", zips: "91501–91506" },
    { name: "Long Beach", zips: "90802, 90804" },
    { name: "Torrance", zips: "90501, 90503" },
    { name: "Inglewood", zips: "90301–90305" },
    { name: "Culver City", zips: "90230, 90232" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent LA departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-flight-takeoff-line", title: "LAX Global Gateway Expertise", desc: "World's 3rd busiest airport — 90+ airlines, 100+ international destinations. We know airline timelines and last-minute boarding scenarios" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in San Diego", route: "/san-diego" },
    { name: "Emergency passport help in San Francisco", route: "/san-francisco" },
    { name: "Urgent passport services in San Jose", route: "/san-jose" },
    { name: "Same-day passport help in Sacramento", route: "/sacramento" },
    { name: "California passport services overview", route: "/states/california" },
  ],
  fpcOfficeNote: "Visit the Fast Passport Center office at 355 S Grand Ave, Suite 2450, in downtown Los Angeles by appointment only. Sign up on our website first to schedule your visit — walk-ins are not accepted. We offer full-service expedited and emergency passport processing for Los Angeles and Southern California travelers.",
  fpcOfficeAddress: "355 S Grand Ave, Ste 2450, Los Angeles, CA 90071",
  fpcOfficeDistance: "355 S Grand Ave, Ste 2450 — Downtown Los Angeles",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the sprawling Los Angeles metropolitan area — from the studio lots of Burbank to the beaches of Santa Monica, from the entertainment power centers of Hollywood and Beverly Hills to the global trade corridors of Long Beach and San Pedro. LA's passport needs are as diverse as its population: an actor needs a UK work visa for a Pinewood Studios shoot, a USC filmmaker departs for Cannes, a Korean-American family visits relatives in Seoul, and an international trade attorney flies to Singapore for arbitration — all on 2 weeks notice or less.",
  nearbyCitiesIntro: "We serve all LA neighborhoods and nearby cities including Burbank, Glendale, Culver City, Inglewood, Torrance, and the broader LA County region. If you are in LA County or nearby Orange County, we have you covered.",
  howItWorksIntro: "Most Los Angeles travelers complete everything online and ship documents overnight. No need to navigate downtown traffic or the 110 freeway to reach the passport agency. Our process is built for LA's pace — where international travel opportunities materialize fast and standard government timelines cannot keep up.",
  mistakesIntro: "Los Angeles is the entertainment capital of the world, home to the nation's busiest international airport, and one of the most ethnically diverse cities on Earth. LA travelers face passport challenges that reflect this unique profile — from work visa timing for film productions to 6-month passport validity rules for Asia-Pacific family visits.",
  testimonialsIntro: "Los Angeles travelers — from Hollywood producers to USC students to Westside families — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Los Angeles? Here are the most common ones we hear from LA travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  servicesIntro: "Whether you need a renewal before a LAX flight to Tokyo, an emergency passport for a Cannes film festival premiere, or first-time passports for an LA family's first international vacation — Fast Passport Center handles it with reserved courier submission availability. As a registered State Department courier (not a reseller), we guarantee the best price and typically cost 30–100% less than FedEx, Staples, and other third-party passport courier resellers.",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "LAX flight to Tokyo in 3 days? Cannes film festival premiere next week? Our emergency courier service submits via reserved agency slots — as fast as 24 hours for LA travelers.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help LA residents renew fast — no agency visit required. Perfect for entertainment professionals, frequent flyers, and Westside families.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants require an in-person acceptance clerk visit. We guide LA residents through every step with clear instructions for the nearest acceptance location.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Left your passport at a film set in Burbank? Stolen from your car in Hollywood? We guide you through DS-64 and replacement fast.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We help LA families navigate the process for kids — from first passport to renewal.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage (hello, LA wedding season), divorce, or a legal name change — we assist with corrected passports so your travel documents match your reservations.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport",
      description: "Need a second passport for frequent international travel? Ideal for entertainment industry professionals, international business executives, and global creatives who travel while their primary passport is in visa processing.",
      bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
    {
      icon: "ri-file-damage-line",
      title: "Damaged Passport",
      description: "Poolside water damage in Beverly Hills? Coffee spill during a West Hollywood production meeting? We guide LA residents through damaged passport replacement fast.",
      bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "The passport card is a wallet-size travel document for land and sea crossings to Mexico, Canada, the Caribbean, and Bermuda. Perfect for LA-to-Tijuana or Cabo weekend trips, and cruise identification.",
      bullets: ["Wallet-size convenience", "Land and sea travel to Mexico", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Los Angeles CA", description: "Urgent passport processing for Los Angeles travelers with immediate departure needs from LAX." },
    { name: "Expedited Passport Renewal Los Angeles CA", description: "Fast passport renewal for Los Angeles residents with a previously-issued passport." },
    { name: "First-Time Passport Los Angeles CA", description: "New passport application services for Los Angeles residents applying for their first passport." },
    { name: "Child Passport Los Angeles CA", description: "Minor passport services for Los Angeles families with children under 16." },
    { name: "Name Change Passport Los Angeles CA", description: "Passport name correction after marriage, divorce, or legal name changes for Los Angeles residents." },
    { name: "Second Passport Los Angeles CA", description: "Second passport services for LA entertainment professionals and frequent international travelers." },
  ],
  testimonials: [
    {
      name: "Marcus T.",
      location: "Santa Monica, CA",
      rating: 5,
      text: "My flight to Tokyo was in 3 days and I realized my passport was expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved my business trip.",
      service: "Emergency Passport",
    },
    {
      name: "Jennifer L.",
      location: "Pasadena, CA",
      rating: 5,
      text: "Needed to renew my passport for a family vacation to Cancun. Process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "David R.",
      location: "Hollywood, CA",
      rating: 5,
      text: "Production company booked me for a shoot at Pinewood Studios in London with 10 days notice. My passport was expired. Fast Passport Center got it done in 3 days — I made the call time.",
      service: "Emergency Renewal",
    },
    {
      name: "Sarah K.",
      location: "Beverly Hills, CA",
      rating: 5,
      text: "As an entertainment attorney who travels internationally 8+ times a year, I cannot afford passport delays. Fast Passport Center has been my go-to for years. Professional, fast, and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "la-1",
      name: "Downtown LA Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1001 N Alameda St",
      city: "Los Angeles",
      state: "CA",
      zip: "90012",
      phone: "(213) 617-7103",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown LA",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "la-2",
      name: "Hollywood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1615 Wilcox Ave",
      city: "Hollywood",
      state: "CA",
      zip: "90028",
      phone: "(323) 464-0534",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3.2 miles from Hollywood Blvd",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "la-3",
      name: "Santa Monica Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1650 5th St",
      city: "Santa Monica",
      state: "CA",
      zip: "90401",
      phone: "(310) 393-5967",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from Santa Monica Pier",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular location for Westside residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "la-4",
      name: "Pasadena City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "100 N Garfield Ave",
      city: "Pasadena",
      state: "CA",
      zip: "91101",
      phone: "(626) 744-4121",
      hours: "Mon–Thu: 7:30 AM – 5:30 PM, Fri: 8:00 AM – 5:00 PM",
      distance: "0.2 miles from Pasadena City Hall",
      website: "https://www.cityofpasadena.net/city-clerk/",
      websiteLabel: "Pasadena City Clerk",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-government-line",
    },
    {
      id: "la-5",
      name: "Burbank Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2230 N Hollywood Way",
      city: "Burbank",
      state: "CA",
      zip: "91505",
      phone: "(818) 842-6141",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.5 miles from Burbank Airport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for San Fernando Valley residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "la-6",
      name: "LA County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "12400 Imperial Hwy",
      city: "Norwalk",
      state: "CA",
      zip: "90650",
      phone: "(562) 462-2133",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "15 miles from downtown LA",
      website: "https://www.lavote.net/",
      websiteLabel: "LA County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA, 110 freeway traffic", fpc: "No — ship from home or office" },
    { feature: "Entertainment/Film Travel", agency: "No industry-specific expertise", fpc: "Work visa timing, production schedules, Cannes/TIFF/Sundance deadlines understood" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle entire production teams, families, or travel groups in parallel" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-film-line",
      title: "Work Visa & Production Schedule Overlaps",
      description: "LA entertainment professionals often need both passport renewal AND a UK, Canada, or Australia work visa for productions. Visa processing takes 2–6 weeks — and the passport must be valid throughout. Start passport renewal BEFORE the visa application process.",
    },
    {
      icon: "ri-time-line",
      title: "LAX Booking-to-Departure Windows Are Deceptive",
      description: "LAX has 90+ airlines serving 100+ international destinations. Flights to Asia, Europe, and Latin America are abundant — and LA travelers often book 1–2 weeks out. Standard passport processing cannot keep pace with LA's last-minute travel culture.",
    },
    {
      icon: "ri-image-line",
      title: "Passport Photo Standards at LA Photo Shops",
      description: "Many LA photo services — including those near studios and tourist areas — produce photos that meet headshot standards but fail passport requirements. Photos must be exactly 2×2 inches, white background, no glasses, neutral expression.",
    },
    {
      icon: "ri-global-line",
      title: "Asia-Pacific Family Travel — 6-Month Rule",
      description: "LA has the largest Korean, Filipino, Thai, and Vietnamese populations outside their home countries. Family visits to Asia require passport validity of 6+ months beyond the return date. Many travelers discover this at the check-in counter.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form at Acceptance Facilities",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at LA acceptance facilities. Our document review catches these before you waste an appointment slot.",
    },
    {
      icon: "ri-road-map-line",
      title: "Underestimating LA Traffic to the Passport Agency",
      description: "The LA Passport Agency is at 11000 Wilshire Blvd Suite 1000 in West LA. From Santa Monica, that is 15–30 minutes. From the Valley, budget 30–45 minutes. Our courier service eliminates this entirely.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes from your home, studio lot, or Westside office." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist and document review guidance — entertainment, family, or business travel." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most LA clients drop at any UPS Store or FedEx Office — no office visit needed for renewals." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — bypassing the LA Passport Agency appointment bottleneck and downtown traffic entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No 110 freeway, no waiting at the federal building — just your passport, ready for travel." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Los Angeles?", a: "Fast Passport Center offers emergency passport processing in Los Angeles in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities. This is the fastest reliable option for LA travelers with urgent international flights from LAX." },
    { q: "Can you help with passports for film and entertainment productions?", a: "Absolutely. We regularly assist LA entertainment professionals — actors, producers, directors, cinematographers — with expedited passports for international productions. We understand work visa timelines, Pinewood Studios shoots, Cannes and Toronto film festivals, and the tight booking windows common in the industry." },
    { q: "How much does expedited passport service cost in Los Angeles?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour, 3–5 day expedited, or standard options. Contact us for a personalized quote." },
    { q: "Do I need to visit the Los Angeles Passport Agency downtown?", a: "No. With Fast Passport Center, you complete everything online and ship documents overnight. Our couriers handle the in-person delivery. You only need to visit a local acceptance clerk if you're a first-time applicant." },
    { q: "What if I need a passport AND a work visa for an international production?", a: "Start with passport renewal first. Many countries — UK, Canada, Australia — require 6 months of passport validity for work visa issuance. We can expedite your passport, then you proceed with the visa application. This two-step approach is standard for LA entertainment travel." },
    { q: "Where can I get passport photos in Los Angeles?", a: "Walgreens, CVS, and FedEx Office locations throughout LA offer passport photos. Many locations in Hollywood, Santa Monica, and Pasadena provide walk-in service. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What airports do LA travelers use internationally?", a: "Los Angeles International Airport (LAX) is the primary gateway — the world's 3rd busiest airport with 90+ airlines serving 100+ international destinations. Burbank (BUR) and Long Beach (LGB) offer limited international service. Ontario (ONT) serves domestic and some Mexico routes." },
    { q: "Does the LA Passport Agency take walk-ins?", a: "The LA Passport Agency (11000 Wilshire Blvd Suite 1000) requires appointments for life-or-death emergency travel only. Walk-ins are not accepted. For travel within 3–14 days, our registered courier service is the fastest legally available channel without agency restrictions." },
    { q: "Can you handle passports for an entire production team?", a: "Yes. We regularly process passports for production teams — from 5-person camera crews to 20-person art departments heading to international shoots. We handle applications in parallel and coordinate timelines." },
    { q: "What about passport cards for Mexico travel from LA?", a: "Passport cards are valid for land and sea crossings to Mexico, Canada, and the Caribbean. For LA residents who drive to Tijuana, Rosarito, or take cruises to Ensenada, a passport card is a convenient, wallet-size option. We expedite passport card applications with the same courier service." },
  ],
  travelTiming: {
    title: "Travel & Passport Timing for Los Angeles Residents",
    intro: "Los Angeles is the entertainment capital of the world and home to one of the planet's busiest international airports. LA's travel patterns are uniquely shaped by the entertainment industry, global trade, and deep-rooted international communities. Here's what LA travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "LAX — Los Angeles International Airport",
        subtitle: "World's 3rd busiest airport — 90+ airlines, 100+ international destinations",
        description: "LAX handles 88+ million passengers annually and is the primary U.S. gateway to Asia-Pacific, Latin America, and Oceania. With nonstop flights to Tokyo, Seoul, Sydney, London, Dubai, and virtually every major global city, LAX creates a constant stream of international travel demand. The airport's sheer volume means LA travelers can book last-minute international flights on dozens of carriers — but expired passports turn a deal into a disaster.",
        tip: "LAX serves 90+ airlines, each with different check-in and documentation requirements. Always verify passport validity the moment you book any international flight. Airlines at LAX are strict about 6-month validity rules for Asia and the Middle East.",
      },
      {
        icon: "ri-film-line",
        title: "Entertainment Industry — Film, TV & Music Production Travel",
        subtitle: "Hollywood, Burbank, Culver City — the global entertainment capital",
        description: "Los Angeles is the world's entertainment production center. Actors fly to London's Pinewood Studios for Marvel and Star Wars productions. Directors attend Cannes, Venice, and Toronto film festivals. Musicians tour internationally with crews of 20+. Production accountants and location scouts deploy to New Zealand, Australia, and Eastern Europe for months-long shoots. Work visa applications for these productions require valid passports with 6+ months of remaining validity — a pipeline that collapses if the passport is expired.",
        tip: "UK, Canada, and Australia work visas for entertainment productions require 6 months of passport validity at time of application. Start passport renewal 3 months before your visa appointment — this gives you buffer for both processes.",
      },
      {
        icon: "ri-global-line",
        title: "International Communities & Heritage Travel",
        subtitle: "Largest Korean, Filipino, Armenian, Thai, Salvadoran, and Iranian populations outside their home countries",
        description: "Los Angeles is arguably the most internationally connected city in the United States. With the largest diasporas of Koreans, Filipinos, Armenians, Thais, Salvadorans, Iranians, and Guatemalans outside their home countries, heritage travel drives a massive volume of passport demand. These trips are often multi-generational — grandparents traveling with grandchildren, extended family reunions, weddings in ancestral hometowns. Many travelers in these communities are first-time passport applicants navigating a complex process for the first time.",
        tip: "First-time passport applicants in LA's immigrant and diaspora communities should start 12+ weeks before travel. Acceptance facility visits are required, and appointments book up during summer and holiday seasons. We guide first-time applicants through every step.",
      },
      {
        icon: "ri-ship-line",
        title: "Port of Los Angeles & Long Beach — Cruise & Trade Hub",
        subtitle: "Busiest container port in the Western Hemisphere, major cruise departure terminal",
        description: "The twin ports of Los Angeles and Long Beach form the largest port complex in the Americas, handling 40% of all U.S. container imports. The cruise terminals at San Pedro and Long Beach serve major lines including Carnival, Princess, and Royal Caribbean with departures to the Mexican Riviera, Hawaii, and through the Panama Canal. International trade professionals — freight forwarders, customs brokers, supply chain managers — travel frequently to Asia and Latin America, often on short notice when a shipment issue arises.",
        tip: "Cruise passengers from San Pedro and Long Beach terminals should verify passport validity 3 months before departure. Many Mexican Riviera cruises require 6 months of validity — even for closed-loop itineraries.",
      },
    ],
    callout: {
      icon: "ri-earth-line",
      title: "LA's Global Production Economy & Short-Notice Travel Culture",
      description: "In Los Angeles, international travel opportunities materialize at the speed of a phone call — a director gets attached to a project in Budapest, a music producer books studio time in London, a trade attorney flies to Singapore for arbitration. Standard government passport processing timelines are fundamentally incompatible with LA's booking-to-departure culture. Our expedited courier service exists to bridge that gap.",
    },
  },
  destinations: {
    title: "Where Los Angeles Travelers Go — And Why Passports Get Urgent",
    intro: "LA's entertainment, trade, and diaspora connections create the most diverse international travel patterns of any U.S. city.",
    stats: [
      { stat: "88M+", label: "passengers through LAX annually — world's 3rd busiest airport" },
      { stat: "1–3 Weeks", label: "average lead time from trip booking to international departure for entertainment professionals" },
      { stat: "35%", label: "of LA expedited requests are entertainment industry or production-related" },
      { stat: "24 Hours", label: "shortest turnaround for an actor called to Pinewood Studios with 4 days notice" },
    ],
    destinations: [
      { name: "United Kingdom", context: "LAX→London multiple daily nonstops across 5+ airlines, Pinewood Studios productions, West End theater, music industry, fashion", urgency: "Production call sheets and filming schedules are immovable", icon: "ri-map-pin-2-line" },
      { name: "Japan & South Korea", context: "LAX→Tokyo/Seoul multiple daily nonstops, Koreatown family visits (largest Korean diaspora), anime/gaming industry, K-pop entertainment", urgency: "Family events and entertainment industry deadlines", icon: "ri-map-pin-2-line" },
      { name: "Mexico & Latin America", context: "LAX has the most flights to Mexico of any U.S. airport, family heritage visits, film location scouting in Mexico City, Baja California weekend trips", urgency: "Year-round demand — not seasonal tourism", icon: "ri-map-pin-2-line" },
      { name: "China & Taiwan", context: "LAX→Shanghai/Beijing/Taipei nonstops, San Gabriel Valley diaspora connections, film co-productions, trade and manufacturing", urgency: "Visa + passport timeline overlap — start renewal early", icon: "ri-map-pin-2-line" },
      { name: "Australia & New Zealand", context: "LAX→Sydney/Melbourne/Auckland nonstops, film production (Weta Workshop, Marvel Studios), surf and adventure travel from LA's beach culture", urgency: "Production deployment and location scout travel", icon: "ri-map-pin-2-line" },
      { name: "France & Italy", context: "Cannes Film Festival, Milan Fashion Week, luxury brand partnerships (Rodeo Drive connections), culinary and wine tourism", urgency: "Film festival and fashion week dates are fixed and public", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other California Service Areas",
    crossLinksDesc: "We serve travelers across the Golden State with the same fast, reliable passport processing.",
    crossLinks: [
      { name: "San Diego", route: "/san-diego" },
      { name: "San Francisco", route: "/san-francisco" },
      { name: "Sacramento", route: "/sacramento" },
    ],
  },
  photoLocations: [
    { name: "CVS Pharmacy — Hollywood", address: "1234 N Vermont Ave, Los Angeles, CA 90029", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Santa Monica", address: "1400 Wilshire Blvd, Santa Monica, CA 90403", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Downtown LA", address: "444 S Flower St, Los Angeles, CA 90071", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Beverly Hills", address: "9641 S Santa Monica Blvd, Beverly Hills, CA 90210", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Torrance", address: "22015 Hawthorne Blvd, Torrance, CA 90503", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Pasadena", address: "200 N Lake Ave, Pasadena, CA 91101", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Burbank", address: "2211 N San Fernando Blvd, Burbank, CA 91504", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Inglewood", address: "3550 W Century Blvd, Inglewood, CA 90303", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    { name: "DMV — Hollywood", address: "8033 Sunset Blvd, Los Angeles, CA 90046", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID and driver's license renewals. Appointment required.", featured: true },
    { name: "DMV — Santa Monica", address: "2235 Colorado Ave, Santa Monica, CA 90404", hours: "Mon–Fri: 8AM–5PM", note: "Passport-ready photo services available. Online appointment booking.", featured: false },
    { name: "DMV — Pasadena", address: "49 S Rosemead Blvd, Pasadena, CA 91107", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID processing. Walk-ins limited — book online first.", featured: false },
    { name: "DMV — Norwalk", address: "12400 Imperial Hwy, Norwalk, CA 90650", hours: "Mon–Fri: 8AM–5PM", note: "Full-service mega center. Longest hours, most services.", featured: true },
  ],
  airports: [
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "12 miles from downtown LA",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "World's 3rd busiest airport — 90+ airlines, 100+ international destinations. The primary U.S. gateway to Asia-Pacific and Latin America. Nonstop flights to every inhabited continent.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: true,
    },
    {
      code: "BUR",
      name: "Hollywood Burbank Airport",
      distance: "15 miles from Hollywood",
      address: "2627 N Hollywood Way, Burbank, CA 91505",
      note: "Smaller airport with domestic flights and some international connections. Easier access than LAX for Valley and Burbank residents. Popular with entertainment industry travelers.",
      flightUrl: "https://www.hollywoodburbankairport.com/",
      flightLabel: "Burbank Airport",
      featured: false,
    },
    {
      code: "LGB",
      name: "Long Beach Airport",
      distance: "25 miles from downtown LA",
      address: "4100 Donald Douglas Dr, Long Beach, CA 90808",
      note: "Convenient for South Bay and Orange County travelers. Domestic flights with some international connections to Mexico.",
      flightUrl: "https://www.longbeach.gov/lgb/",
      flightLabel: "Long Beach Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the California Department of Public Health Vital Records. Online, mail, or in-person options available for Los Angeles County residents. Processing typically takes 5–10 business days.",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through California CDPH Vital Records. Required for estate and legal matters in LA County.",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage license records from the Los Angeles County Registrar-Recorder/County Clerk. Certified copies available for name changes and legal purposes.",
      url: "https://www.lavote.net/",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Los Angeles County Superior Court. Required for name change passport corrections and legal documentation.",
      url: "https://www.lacourt.org/",
    },
  ],
};